import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { getAuthUser, canManageCarrierUsers, logout } from '../../services/authApi';
import {
  fetchCarrierQuotesPage, fetchCarrierQuotesSummary, fetchCarrierUsers, fetchQuoteAssignments, scopeLabel,
  QUOTES_BLOCK_SIZE, Quote, QuotesSummary, CarrierUser,
} from '../../services/carrierApi';
import {
  StatGrid, PaginatedQuoteTable, CapabilityList, icons,
} from '../../components/dashboard/DashboardKit';
import AssignSubCarriersModal from '../../components/dashboard/AssignSubCarriersModal';

/* Carrier Admin / Manager — IsCarrierUser = 1.
   Every read is scoped to this user's CarrierId. */

/** Assigned To cell. Mounts only for rows on the visible page, and asks for that
 *  quote's assignments once — there is no bulk endpoint to ask for them all. */
const AssigneeCell: React.FC<{
  quote: Quote;
  /** undefined = not loaded yet, null = the lookup failed */
  names: string[] | null | undefined;
  interactive: boolean;
  onLoad: (quote: Quote) => void;
  onOpen: (quote: Quote) => void;
}> = ({ quote, names, interactive, onLoad, onOpen }) => {
  useEffect(() => {
    if (interactive) onLoad(quote);
  }, [interactive, quote, onLoad]);

  const label =
    names === undefined && interactive ? 'Loading…'
    : !names ? (names === null ? '—' : 'Unassigned')
    : names.length === 0 ? 'Unassigned'
    : `${names.length} user${names.length === 1 ? '' : 's'}`;
  const title = names && names.length > 0 ? names.join(', ') : undefined;

  if (!interactive) return <span className="db-date" title={title}>{label}</span>;

  return (
    <button
      type="button"
      className={`assign-cell${names && names.length > 0 ? '' : ' assign-cell--empty'}`}
      title={title ?? 'Assign users'}
      onClick={() => onOpen(quote)}
    >
      {label}
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </button>
  );
};

const CarrierDashboard: React.FC = () => {
  const navigate = useNavigate();
  const user = getAuthUser();
  const toast = useRef<Toast>(null);
  // Only a Carrier account may assign; anyone else sees plain text.
  const canAssign = user?.userType === 'Carrier';

  const [users, setUsers] = useState<CarrierUser[]>([]);
  const [summary, setSummary] = useState<QuotesSummary | null>(null);
  const [search, setSearch] = useState('');
  // The search the loaded quotes belong to: `search` after a short pause in typing.
  const [query, setQuery] = useState('');
  // Quotes are fetched from the server in blocks of QUOTES_BLOCK_SIZE (the largest page size),
  // only when the visible page needs them — never the whole list.
  const [blocks, setBlocks] = useState<Record<number, Quote[]>>({});
  const [total, setTotal] = useState(0);
  const [range, setRange] = useState({ start: 0, size: 10 });
  const [pending, setPending] = useState(0);
  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;
  const inflight = useRef<Set<number>>(new Set());
  // Bumped on every new search so a slow response for an old one is dropped.
  const generation = useRef(0);
  const [assigning, setAssigning] = useState<Quote | null>(null);
  // Names assigned per quote, keyed by NowCerts databaseId. null = lookup failed.
  const [assignments, setAssignments] = useState<Record<string, string[] | null>>({});
  const requested = useRef<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    // Independent loads: a failure in one shouldn't blank the others.
    fetchCarrierQuotesSummary()
      .then(sm => !cancelled && setSummary(sm))
      .catch(err => console.error('Could not load quote totals:', err));
    fetchCarrierUsers()
      .then(u => !cancelled && setUsers(u.data))
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => setQuery(search), 400);
    return () => window.clearTimeout(t);
  }, [search]);

  // A new search starts over: drop what was loaded (the table goes back to page 1 as the total resets).
  useEffect(() => {
    generation.current += 1;
    inflight.current.clear();
    blocksRef.current = {};
    setBlocks({});
    setTotal(0);
    setPending(0);
  }, [query]);

  const loadBlock = useCallback((block: number) => {
    if (blocksRef.current[block] || inflight.current.has(block)) return;
    inflight.current.add(block);
    setPending(n => n + 1);
    const gen = generation.current;
    fetchCarrierQuotesPage(block * QUOTES_BLOCK_SIZE, QUOTES_BLOCK_SIZE, query)
      .then(res => {
        if (gen !== generation.current) return;
        setBlocks(prev => ({ ...prev, [block]: res.rows }));
        setTotal(res.total);
      })
      .catch(err => {
        if (gen !== generation.current) return;
        console.error('Could not load quotes:', err);
        toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not load quotes.', life: 4000 });
      })
      .finally(() => {
        if (gen !== generation.current) return;
        inflight.current.delete(block);
        setPending(n => n - 1);
      });
  }, [query]);

  // Load whichever block(s) hold the page on screen — a page of up to 100 rows can straddle two.
  useEffect(() => {
    const first = Math.floor(range.start / QUOTES_BLOCK_SIZE);
    const last = Math.floor((range.start + range.size - 1) / QUOTES_BLOCK_SIZE);
    for (let b = first; b <= last; b++) loadBlock(b);
  }, [range, loadBlock]);

  const handleRange = useCallback((start: number, size: number) => {
    setRange(prev => (prev.start === start && prev.size === size ? prev : { start, size }));
  }, []);

  const pageRows = useMemo(() => {
    const rows: Quote[] = [];
    const end = Math.min(range.start + range.size, total);
    for (let i = range.start; i < end; i++) {
      const row = blocks[Math.floor(i / QUOTES_BLOCK_SIZE)]?.[i % QUOTES_BLOCK_SIZE];
      if (row) rows.push(row);
    }
    return rows;
  }, [blocks, range, total]);

  const notify = (severity: 'success' | 'error', detail: string) =>
    toast.current?.show({
      severity,
      summary: severity === 'success' ? 'Done' : 'Error',
      detail,
      life: severity === 'success' ? 2600 : 4000,
    });

  const loadAssignments = useCallback((q: Quote) => {
    const id = q.databaseId;
    if (!id || requested.current.has(id)) return;
    requested.current.add(id);
    fetchQuoteAssignments(id)
      .then(list => setAssignments(prev =>
        // A save or modal open that landed first is newer than this response.
        id in prev ? prev : { ...prev, [id]: list.map(a => a.fullName) }))
      .catch(err => {
        if (err?.status === 401) { logout(); navigate('/login'); return; }
        setAssignments(prev => (id in prev ? prev : { ...prev, [id]: null }));
      });
  }, [navigate]);

  const renderAssignee = (q: Quote) => (
    <AssigneeCell
      quote={q}
      names={q.databaseId ? assignments[q.databaseId] : []}
      interactive={canAssign && !!q.databaseId}
      onLoad={loadAssignments}
      onOpen={setAssigning}
    />
  );

  const stats = [
    {
      label: 'Total Quotes',
      value: summary?.total ?? 0,
      accent: '#0d6efd', bg: '#eff6ff', icon: icons.doc,
      hint: 'Your carrier only',
    },
    {
      label: 'Bound Policies',
      value: summary?.bound ?? 0,
      accent: '#059669', bg: '#ecfdf5', icon: icons.check,
      hint: 'Converted to policy',
    },
    {
      label: 'Pending',
      value: summary?.pending ?? 0,
      accent: '#d97706', bg: '#fffbeb', icon: icons.clock,
      hint: 'Awaiting action',
    },
    {
      label: 'Team Members',
      value: users.length,
      accent: '#6f42c1', bg: '#f5f3ff', icon: icons.users,
      hint: `${users.filter(u => u.isActive).length} active`,
    },
  ];

  return (
    <div className="dashboard">
      <Toast ref={toast} />
      <StatGrid stats={stats} />

      <PaginatedQuoteTable
        quotes={pageRows}
        remote={{ total, onRange: handleRange }}
        title="Recent Quotes"
        subtitle={`Submissions for ${scopeLabel(user)}`}
        showAssignee
        renderAssignee={renderAssignee}
        showDetails
        onViewDetails={q =>
          navigate(`/carrier/quote/${encodeURIComponent(q.insuredDatabaseId || q.databaseId || q.id)}`, {
            state: { quoteId: q.id, status: q.status, company: q.insuredName },
          })}
        emptyLabel={pending > 0 ? 'Loading quotes…' : 'No records found'}
        headerRight={
          <div className="db-table-card__actions">
            <div className="search-box search-box--sm">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search ID or insured…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            {canManageCarrierUsers(user) && (
              <button className="btn-primary btn-primary--sm" onClick={() => navigate('/carrier/users')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <line x1="20" y1="8" x2="20" y2="14" />
                  <line x1="23" y1="11" x2="17" y2="11" />
                </svg>
                Manage Users
              </button>
            )}
          </div>
        }
      />

      <div className="cap-grid">
        <CapabilityList
          title="What you can do"
          items={[
            'View all carrier quotes (own carrier only)',
            'Manage endorsements',
            'View reports',
            'User management (add, edit, delete users)',
            'Assign submissions',
            'Carrier settings',
          ]}
        />
      </div>

      {assigning && (
        <AssignSubCarriersModal
          quote={assigning}
          onClose={() => setAssigning(null)}
          onAssignmentChange={(q, names) =>
            q.databaseId && setAssignments(prev => ({ ...prev, [q.databaseId!]: names }))
          }
          notify={notify}
        />
      )}
    </div>
  );
};

export default CarrierDashboard;
